import { SECURITY_CONFIG } from "./security-config";

export interface PasswordValidationResult {
  isValid: boolean;
  errors: string[];
  strength: "weak" | "medium" | "strong";
}

/**
 * Validate password against security policy
 */
export function validatePassword(password: string): PasswordValidationResult {
  const errors: string[] = [];
  let strength: "weak" | "medium" | "strong" = "weak";
  let strengthScore = 0;

  // Check minimum length
  if (password.length < SECURITY_CONFIG.password.minLength) {
    errors.push(
      `Password must be at least ${SECURITY_CONFIG.password.minLength} characters long`
    );
  } else {
    strengthScore++;
  }

  // Check for uppercase letters
  if (SECURITY_CONFIG.password.requireUppercase && !/[A-Z]/.test(password)) {
    errors.push("Password must contain at least one uppercase letter");
  } else if (/[A-Z]/.test(password)) {
    strengthScore++;
  }

  // Check for lowercase letters
  if (SECURITY_CONFIG.password.requireLowercase && !/[a-z]/.test(password)) {
    errors.push("Password must contain at least one lowercase letter");
  } else if (/[a-z]/.test(password)) {
    strengthScore++;
  }

  // Check for numbers
  if (SECURITY_CONFIG.password.requireNumbers && !/\d/.test(password)) {
    errors.push("Password must contain at least one number");
  } else if (/\d/.test(password)) {
    strengthScore++;
  }

  // Check for special characters
  if (
    SECURITY_CONFIG.password.requireSpecialChars &&
    !/[!@#$%^&*(),.?":{}|<>]/.test(password)
  ) {
    errors.push("Password must contain at least one special character");
  } else if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
    strengthScore++;
  }

  // Additional strength checks
  if (password.length >= 12) strengthScore++;
  if (password.length >= 16) strengthScore++;

  // Check for common patterns
  const commonPatterns = [
    /^123456/,
    /^password/i,
    /^qwerty/i,
    /^abc123/i,
    /^admin/i,
    /^letmein/i,
  ];

  if (commonPatterns.some((pattern) => pattern.test(password))) {
    errors.push("Password is too common, please choose a more unique password");
    strengthScore = Math.max(0, strengthScore - 2);
  }

  // Determine strength
  if (strengthScore >= 6) {
    strength = "strong";
  } else if (strengthScore >= 4) {
    strength = "medium";
  } else {
    strength = "weak";
  }

  return {
    isValid: errors.length === 0,
    errors,
    strength,
  };
}

/**
 * Generate a random secure password
 */
export function generateSecurePassword(length: number = 16): string {
  const lowercase = "abcdefghijklmnopqrstuvwxyz";
  const uppercase = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const numbers = "0123456789";
  const special = "!@#$%^&*()_+-=[]{}|;:,.<>?";

  let charset = lowercase;
  let password = "";

  // Ensure at least one of each required type
  if (SECURITY_CONFIG.password.requireUppercase) {
    charset += uppercase;
    password += uppercase[Math.floor(Math.random() * uppercase.length)];
  }

  if (SECURITY_CONFIG.password.requireLowercase) {
    password += lowercase[Math.floor(Math.random() * lowercase.length)];
  }

  if (SECURITY_CONFIG.password.requireNumbers) {
    charset += numbers;
    password += numbers[Math.floor(Math.random() * numbers.length)];
  }

  if (SECURITY_CONFIG.password.requireSpecialChars) {
    charset += special;
    password += special[Math.floor(Math.random() * special.length)];
  }

  // Fill the rest randomly
  for (let i = password.length; i < length; i++) {
    password += charset[Math.floor(Math.random() * charset.length)];
  }

  // Shuffle the password
  return password
    .split("")
    .sort(() => Math.random() - 0.5)
    .join("");
}
